import React from 'react';
import { 
  ShieldCheckIcon, 
  LockClosedIcon, 
  DocumentCheckIcon,
  ServerIcon,
  KeyIcon,
  EyeIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

/**
 * Trust Page - Security, Compliance, and Privacy Information
 * 
 * Public page showcasing security features, compliance status, and privacy practices.
 * Similar to trust.juma.ai
 * 
 * Route: /security
 */
export default function TrustPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-medium text-gray-900 sm:text-5xl">
              Trust & Security
            </h1>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Your data security and privacy are our top priorities. 
              Learn about our security practices, compliance certifications, and privacy commitments.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Security Features */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <h2 className="text-3xl font-medium text-gray-900">Security Features</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SecurityFeatureCard
              icon={<LockClosedIcon className="h-6 w-6" />}
              title="Encryption"
              description="All data is encrypted in transit (TLS 1.3) and at rest. HTTPS enforced across all connections."
            />
            <SecurityFeatureCard
              icon={<ShieldCheckIcon className="h-6 w-6" />}
              title="Security Headers"
              description="Helmet.js security headers including CSP, HSTS, X-Frame-Options, and more to protect against common attacks."
            />
            <SecurityFeatureCard
              icon={<EyeIcon className="h-6 w-6" />}
              title="Rate Limiting"
              description="API rate limiting protects against DDoS attacks and abuse. Different limits for authentication, generation, and general API calls."
            />
            <SecurityFeatureCard
              icon={<KeyIcon className="h-6 w-6" />}
              title="Bot Protection"
              description="Cloudflare protects login and critical operations from bots and automated attacks."
            />
            <SecurityFeatureCard
              icon={<ServerIcon className="h-6 w-6" />}
              title="Secure Infrastructure"
              description="Hosted on Amazon Web Services with Cloudflare protection. Enterprise-grade infrastructure with high availability."
            />
            <SecurityFeatureCard
              icon={<DocumentCheckIcon className="h-6 w-6" />}
              title="Error Handling"
              description="Sanitized error responses prevent information disclosure. Internal errors are logged but not exposed to clients."
            />
            <SecurityFeatureCard
              icon={<LockClosedIcon className="h-6 w-6" />}
              title="CORS Protection"
              description="Cross-Origin Resource Sharing (CORS) is configured to allow only trusted domains, preventing unauthorized access."
            />
            <SecurityFeatureCard
              icon={<ShieldCheckIcon className="h-6 w-6" />}
              title="Data Isolation"
              description="Workspace-based data isolation ensures your data is never shared between accounts. Each workspace is completely isolated."
            />
            <SecurityFeatureCard
              icon={<EyeIcon className="h-6 w-6" />}
              title="Access Controls"
              description="Role-based access controls (RBAC) and authentication ensure only authorized users can access your data."
            />
          </div>
        </section>

        {/* Compliance & Certifications */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
            <h2 className="text-3xl font-medium text-gray-900">Compliance & Security Standards</h2>
          </div>
          
          <div className="">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* GDPR */}
              <ComplianceCard
                title="GDPR Compliant"
                status="compliant"
                description="We are fully compliant with the General Data Protection Regulation (GDPR) for all EU users."
                features={[
                  "Right to access your data",
                  "Right to deletion",
                  "Data portability",
                  "Privacy by design",
                  "Data Processing Agreements (DPA)"
                ]}
              />

              {/* Security Self-Assessment */}
              <ComplianceCard
                title="Security Self-Assessment"
                status="self-assessment"
                description="We maintain enterprise-grade security practices through regular self-assessments and security audits."
                features={[
                  "Rate limiting protection",
                  "Security headers",
                  "Bot protection (Cloudflare)",
                  "Error handling & sanitization",
                  "CORS protection",
                  "Regular security reviews"
                ]}
              />

              {/* Security Best Practices */}
              <ComplianceCard
                title="Security Best Practices"
                status="security-practices"
                description="We follow industry-standard security practices and continuously improve our security posture."
                features={[
                  "Information Security Management",
                  "Risk management",
                  "Continuous improvement",
                  "Regular security audits",
                  "Vulnerability management",
                  "Incident response procedures"
                ]}
              />

              {/* HIPAA - Not Applicable */}
              <ComplianceCard
                title="HIPAA"
                status="not-applicable"
                description="HIPAA compliance is not applicable as we do not process Protected Health Information (PHI)."
                features={[]}
              />
            </div>
          </div>
        </section>

        {/* Privacy Practices */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
           
            <h2 className="text-3xl font-medium text-gray-900">Privacy Practices</h2>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="space-y-6">
              <PrivacyPractice
                title="Data Minimization"
                description="We only collect data that is necessary to provide our services. We don't sell your data to third parties."
              />
              <PrivacyPractice
                title="User Control"
                description="You have full control over your data. Export, edit, or delete your data at any time from your account settings."
              />
              <PrivacyPractice
                title="Transparency"
                description="Our Privacy Policy clearly explains what data we collect, how we use it, and your rights."
              />
              <PrivacyPractice
                title="Data Retention"
                description="We only retain your data for as long as necessary to provide our services or as required by law."
              />
              <PrivacyPractice
                title="Third-Party Services"
                description="We use trusted third-party services (AWS, Directus, Stripe) that are GDPR compliant and have Data Processing Agreements (DPA) in place."
              />
            </div>
          </div>
        </section>

        {/* Security Practices */}
        <section className="mb-16">
          <div className="flex items-center mb-8">
       
            <h2 className="text-3xl font-medium text-gray-900">Security Practices</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <PracticeCard
              title="Vulnerability Management"
              items={[
                "Regular security scans",
                "Automated dependency updates",
                "Penetration testing",
                "Bug bounty program (planned)"
              ]}
            />
            <PracticeCard
              title="Incident Response"
              items={[
                "24/7 security monitoring",
                "Automated threat detection",
                "Incident response procedures",
                "Regular security training"
              ]}
            />
            <PracticeCard
              title="Access Management"
              items={[
                "Multi-factor authentication (MFA)",
                "Role-based access controls",
                "Regular access reviews",
                "Principle of least privilege"
              ]}
            />
            <PracticeCard
              title="Data Protection"
              items={[
                "Encryption at rest and in transit",
                "Regular backups",
                "Disaster recovery plan",
                "Data isolation between workspaces"
              ]}
            />
          </div>
        </section>

        {/* Trusted By */}
        <section className="mb-16">
          <h2 className="text-3xl font-medium text-gray-900 mb-8 text-center">
            Trusted by Teams Worldwide
          </h2>
          <div className="text-center text-gray-600">
            <p className="text-lg mb-4">
              We're committed to maintaining the highest standards of security and privacy.
            </p>
            <p className="text-sm">
              If you have security concerns or want to report a vulnerability, please contact us at{' '}
              <a href="mailto:security@prosecaptain.com" className="text-blue-600 hover:underline">
                security@prosecaptain.com
              </a>
            </p>
          </div>
        </section>

        {/* Links */}
        <section className="border-t border-gray-200 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <LinkCard
              title="Privacy Policy"
              description="Read our complete privacy policy"
              href="/privacy-policy"
            />
            <LinkCard
              title="Terms of Service"
              description="Review our terms and conditions"
              href="/terms-conditions"
            />
            <LinkCard
              title="Security.txt"
              description="Security contact information"
              href="/.well-known/security.txt"
            />
          </div>
        </section>

      </div>
    </div>
  );
}

// Component: Security Feature Card
function SecurityFeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6  ">
      <div className="flex items-start">
        <div className="flex-shrink-0 text-blue-600">
          {icon}
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>
      </div>
    </div>
  );
}

// Component: Compliance Card
function ComplianceCard({ title, status, description, features }) {
  const statusConfig = {
    compliant: {
      icon: <CheckCircleIcon className="h-5 w-5 text-green-600" />,
      label: "Compliant",
      color: "green"
    },
    "self-assessment": {
      icon: <ShieldCheckIcon className="h-5 w-5 text-blue-600" />,
      label: "Active",
      color: "blue"
    },
    "security-practices": {
      icon: <DocumentCheckIcon className="h-5 w-5 text-indigo-600" />,
      label: "Implemented",
      color: "indigo"
    },
    "in-progress": {
      icon: <div className="h-5 w-5 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin" />,
      label: "In Progress",
      color: "yellow"
    },
    planned: {
      icon: <DocumentCheckIcon className="h-5 w-5 text-blue-600" />,
      label: "Planned",
      color: "blue"
    },
    "not-applicable": {
      icon: <XMarkIcon className="h-5 w-5 text-gray-400" />,
      label: "Not Applicable",
      color: "gray"
    }
  };

  const config = statusConfig[status] || statusConfig.planned;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center">
          {config.icon}
          <span className={`ml-2 text-sm font-medium text-${config.color}-600`}>
            {config.label}
          </span>
        </div>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      {features.length > 0 && (
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start text-sm text-gray-600">
              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Component: Privacy Practice
function PrivacyPractice({ title, description }) {
  return (
    <div className="border-l-4 border-purple-500 pl-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

// Component: Practice Card
function PracticeCard({ title, items }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-start text-sm text-gray-600">
            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Component: Link Card
function LinkCard({ title, description, href }) {
  return (
    <a
      href={href}
      className="block bg-white border border-gray-200 rounded-lg p-6  "
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </a>
  );
}
